import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Shield, Award, FileCheck, TrendingUp, Lock, ClipboardCheck, GraduationCap } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo, images } from '../data/mock';

const Hero = () => {
  const navigate = useNavigate();

  const handleScroll = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleServiceClick = (serviceTitle) => {
    // ISO Services scrolls to ISO section on home page
    if (serviceTitle === 'ISO') {
      handleScroll('#iso-services');
    }
    // CMMI Services scrolls to CMMI section on home page
    else if (serviceTitle === 'CMMI') {
      handleScroll('#cmmi-services');
    }
    // CMMC Services scrolls to CMMC section on home page
    else if (serviceTitle === 'CMMC') {
      handleScroll('#cmmc-services');
    }
    // SOC 2 Services scrolls to SOC 2 section on home page
    else if (serviceTitle === 'SOC 2') {
      handleScroll('#soc2-services');
    }
    // Training Services scrolls to Training section on home page
    else if (serviceTitle === 'Training') {
      handleScroll('#training-services');
    } else {
      // Other services scroll to services section
      handleScroll('#services');
    }
  };

  const heroServices = [
    {
      title: 'ISO',
      subtitle: 'Services',
      description: 'ISO 9001, 14001 & 45001, ISO/IEC 20000-1, 27001 & 42001',
      icon: ShieldCheck,
      color: 'from-blue-500 to-green-400'
    },
    {
      title: 'CMMI',
      subtitle: 'Services',
      description: 'CMMI DEV & CMMI SVC ',
      icon: TrendingUp,
      color: 'from-green-400 to-green-600'
    },
    {
      title: 'CMMC',
      subtitle: 'Services',
      description: 'DoD cybersecurity compliance',
      icon: Lock,
      color: 'from-blue-500 to-green-400'
    },
    {
      title: 'SOC 2',
      subtitle: 'Services',
      description: 'Evaluate the effectiveness of controls',
      icon: ClipboardCheck,
      color: 'from-green-400 to-green-600'
    },
    {
      title: 'Training',
      subtitle: 'Services',
      description: 'ITIL, CMMI, ISO Lead Auditor & CISSP',
      icon: GraduationCap,
      color: 'from-blue-500 to-green-400'
    }
  ];

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden pt-20 pb-8 sm:pt-24 sm:pb-10 md:pt-16 md:pb-12 lg:pt-20 lg:pb-16"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={images.office}
          alt="Modern office"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/60 via-slate-900/50 to-slate-900/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-green-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Mission Statement - Top Center */}
        <div className="text-center mb-6 sm:mb-8 md:mb-10 lg:mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/40 rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-5 max-w-full">
            <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-300 flex-shrink-0" />
            <span className="text-blue-300 text-xs sm:text-sm md:text-base lg:text-lg font-semibold break-words">
              20+ Years of Excellence • 150+ Organizations Served
            </span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 sm:mb-5 md:mb-6">
            Your Trusted Partner in{' '}
            <span className="text-white">
              Compliance & Certification
            </span>
          </h1>
          
          <p className="text-sm sm:text-base md:text-lg text-slate-200 max-w-3xl mx-auto leading-relaxed mb-6 sm:mb-8">
            Our mission is to help transform industry standards into engines of growth, utilizing accredited certification as the catalyst for process and performance improvement
          </p>

          {/* CTA Buttons - Removed, moved below RABQSA section */}
        </div>

        {/* Services Grid - Desktop: 5 columns, Mobile: 2 columns with Training centered */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2.5 sm:gap-3 md:gap-3.5 lg:gap-4 mb-6 sm:mb-8 md:mb-10 lg:mb-12">
          {heroServices.slice(0, 4).map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div
                key={service.title}
                onClick={() => handleServiceClick(service.title)}
                className="group cursor-pointer bg-slate-900/60 backdrop-blur-md border border-white/20 rounded-2xl p-3 sm:p-4 md:p-5 hover:bg-slate-900/70 transition-all duration-300 hover:scale-105 hover:border-blue-400/50 flex flex-col items-center text-center"
              >
                {/* Icon */}
                <div className={`w-11 h-11 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-2 sm:mb-3 group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent className={`w-5 h-5 sm:w-6 sm:h-6 ${service.title === 'ISO' || service.title === 'CMMC' ? 'text-white' : 'text-white'}`} />
                </div>
                
                {/* Title */}
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-1">
                  {service.title}
                </h3>
                <p className="text-blue-200 font-medium text-xs sm:text-sm mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-slate-700 w-full">
                  {service.subtitle}
                </p>
                
                {/* Description */}
                <p className="text-slate-200 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4 flex-grow">
                  {service.description}
                </p>

                {/* Hover Arrow */}
                <div className="flex items-center text-blue-300 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-xs sm:text-sm font-medium">Learn More</span>
                  <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            );
          })}
          
          {/* Training - Hidden on mobile in main grid, shown below */}
          <div
            onClick={() => handleScroll('#training-services')}
            className="hidden lg:block group cursor-pointer bg-slate-900/60 backdrop-blur-md border border-white/20 rounded-2xl p-5 hover:bg-slate-900/70 transition-all duration-300 hover:scale-105 hover:border-blue-400/50 flex flex-col items-center text-center"
          >
            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${heroServices[4].color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 mx-auto`}>
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-1">
              {heroServices[4].title}
            </h3>
            <p className="text-blue-200 font-medium text-sm mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-slate-700 w-full">
              {heroServices[4].subtitle}
            </p>
            <p className="text-slate-200 text-sm leading-relaxed mb-4 flex-grow">
              {heroServices[4].description}
            </p>
            <div className="flex items-center text-blue-200 opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-sm font-medium">Learn More</span>
              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>

        {/* Training - Centered on Mobile */}
        <div className="lg:hidden flex justify-center mb-8 sm:mb-10">
          <div
            onClick={() => handleScroll('#training-services')}
            className="group cursor-pointer bg-slate-900/60 backdrop-blur-md border border-white/20 rounded-2xl p-3 sm:p-4 md:p-5 hover:bg-slate-900/70 transition-all duration-300 hover:scale-105 hover:border-blue-400/50 w-[calc(50%-0.375rem)] flex flex-col items-center text-center"
          >
            <div className={`w-11 h-11 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br ${heroServices[4].color} flex items-center justify-center mb-2 sm:mb-3 group-hover:scale-110 transition-transform duration-300 mx-auto`}>
              <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-1">
              {heroServices[4].title}
            </h3>
            <p className="text-blue-200 font-medium text-xs sm:text-sm mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-slate-700 w-full">
              {heroServices[4].subtitle}
            </p>
            <p className="text-slate-200 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4 flex-grow">
              {heroServices[4].description}
            </p>
            <div className="flex items-center text-blue-300 opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-xs sm:text-sm font-medium">Learn More</span>
              <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-4 sm:mt-6 md:mt-8 lg:mt-10 flex flex-wrap justify-center items-center gap-3 sm:gap-4 md:gap-6 lg:gap-8">
          <div className="flex items-center gap-2 text-slate-300">
            <FileCheck className="w-4 h-4 sm:w-5 sm:h-5 text-blue-300 flex-shrink-0" />
            <span className="text-xs sm:text-sm"> Certified Auditors</span>
          </div>
          <div className="flex items-center gap-2 text-slate-300">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-green-300 flex-shrink-0" />
            <span className="text-xs sm:text-sm">ITIL Expert Trainers</span>
          </div>
          <div className="flex items-center gap-2 text-slate-300">
            <Award className="w-4 h-4 sm:w-5 sm:h-5 text-blue-300 flex-shrink-0" />
            <span className="text-xs sm:text-sm">On-Time Project Delivery</span>
          </div>
        </div>

        {/* Get Free Consultation Button - Centered on mobile, right-aligned on desktop */}
        <div className="mt-8 sm:mt-6 md:mt-8 lg:mt-10 flex justify-center lg:justify-end">
          <Button
            onClick={() => handleScroll('#contact')}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 sm:px-6 md:px-7 py-2.5 sm:py-3 text-xs sm:text-sm md:text-base group flex items-center justify-between max-w-xs"
          >
            <span>Get Started Now</span>
            <ArrowRight className="w-3 h-3 text-white group-hover:translate-x-1 transition-transform ml-1" />
          </Button>
        </div>
      </div>

      {/* Scroll Indicator - hidden on mobile, shown on desktop */}
      <div className="hidden sm:flex absolute bottom-8 sm:bottom-12 md:bottom-16 left-1/2 -translate-x-1/2 z-10">
        <div className="w-5 h-9 sm:w-6 sm:h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-2 sm:h-3 bg-white/50 rounded-full mt-2 animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
